/**
 * GraphQL service layer for the mediaDetails state module.
 *
 * Exports thunks that call the Veritone GraphQL API via @helpers/callGraphQLApi and dispatch
 * the matching *_SUCCESS / *_FAILURE actions from ./actions. Covers fetching the TDO and its
 * assets, redacted media, recent redaction-data saves, waveform CSV, case / folder info, media
 * comments, TDO locks, status, name and settings updates, redaction and download job polling,
 * audit-log assets, and asset deletion. Consumed mainly by ./saga/saga.ts; each thunk pairs with a response interface
 * describing the raw query shape. Note: fetchTdo redirects to the file-in-trash route when
 * the loaded TDO is trashed.
 *
 * **Writing `details`: send only the keys you own.** `updateTDO` merges `details`
 * at the TOP level only - each key it is given replaces the server's row for that
 * key wholesale, and a key it is not given is left untouched. So a writer that
 * spreads the whole of redux's `tdo.details` resends every key as redux knows it,
 * and silently overwrites anything redux is behind on. VE-30516: that is how a
 * SAVE came to delete `settings.lastSavedDateTime`, which the stale-redacted-file
 * check judges by. Before adding a `details` writer, name its keys explicitly.
 * `redact` and `veritoneFile` are the only ones that genuinely need reading
 * first, and `settings` belongs to `updateSettingsOnDetails` alone.
 */
import {
  CaseId,
  FolderId,
  lookupLatestMediaCommentsSchemaId,
  lookupLatestTdoLockSchemaId,
  TDOId,
  Thunk,
} from '@cbsa-modules/universal';
import moment from 'moment/moment';
import * as Actions from './actions';
import { get, isEmpty } from 'lodash';
import { modules } from '@veritone/glc-redux';
import { DEFAULT_GLOBAL_SETTINGS, LOCK_REFRESH } from '@helpers/constants';
import { selectConfigEngines } from '@common-modules/engines/selectors';
import {
  local,
  selectGlobalSettings,
  selectHasSpeakerSeparatedTranscript,
  selectSpeakerSeparationAssetIds,
  selectTdo,
  selectTdoLocks,
} from './selectors';
import {
  AudiowaveFrame,
  BaseGlobalSettings,
  DetailTDO,
  MediaComment,
  Tag,
} from './models';
import {
  DEFAULT_REDACTION_STATUS_LABEL,
  mergeRedactionStatusTagFields,
  type RedactionStatusTagWriteFields,
} from './redactionStatuses';
import { fetchFolderQuery } from '@redact/state/modules/casemedia/services/queries';
import { getGlobalSettings } from '@common-modules/mediaDetails/helpers/legacy-global-settings-helper';
import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { redirect } from 'redux-first-router';
import { ROUTE_FILE_IN_TRASH } from '@common-modules/routing';
import {
  DOWNLOAD_ASSET_JOB_STATUS,
  DOWNLOAD_ASSET_JOB_STATUS_SUCCESS,
  DOWNLOAD_ASSET_JOB_STATUS_FAILURE,
  FETCH_DELETE_LAST_ASSET,
  FETCH_DELETE_LAST_ASSET_FAILURE,
  FETCH_DELETE_LAST_ASSET_SUCCESS,
  FETCH_REDACTED_MEDIA,
  FETCH_REDACTED_MEDIA_FAILURE,
  FETCH_REDACTED_MEDIA_SUCCESS,
  LOG_AUDIT_EVENT_INIT,
  LOG_AUDIT_EVENT_SUCCESS,
  LOG_AUDIT_EVENT_FAILURE,
  FETCH_STATUS_CHANGE,
  FETCH_STATUS_CHANGE_FAILURE,
  FETCH_STATUS_CHANGE_SUCCESS,
  FETCH_TDO,
  FETCH_TDO_SUCCESS,
  FETCH_TDO_WITH_FAILURE,
  REFRESH_TDO_TASKS,
  REFRESH_TDO_TASKS_SUCCESS,
  REFRESH_TDO_TASKS_FAILURE,
  REFRESH_TDO_PRIMARY_ASSET_ID,
  REFRESH_TDO_PRIMARY_ASSET_ID_SUCCESS,
  REFRESH_TDO_PRIMARY_ASSET_ID_FAILURE,
  FETCH_UPDATE_REDACTED_FILE_METRICS,
  FETCH_UPDATE_REDACTED_FILE_METRICS_FAILURE,
  FETCH_UPDATE_REDACTED_FILE_METRICS_SUCCESS,
  IN_REDACTION_TAG_KEY,
  POLL_DOWNLOAD_ASSET,
  POLL_DOWNLOAD_ASSET_FAILURE,
  POLL_DOWNLOAD_ASSET_SUCCESS,
  RETRIEVE_TDO_FROM_JOB,
  RETRIEVE_TDO_FROM_JOB_SUCCESS,
  RETRIEVE_TDO_FROM_JOB_FAILURE,
  REDACTION_JOB_STATUS,
  REDACTION_JOB_STATUS_FAILURE,
  REDACTION_JOB_STATUS_SUCCESS,
  FETCH_UPDATE_SETTINGS,
  FETCH_UPDATE_SETTINGS_SUCCESS,
  FETCH_UPDATE_SETTINGS_FAILURE,
  FETCH_LATEST_TDO_AUDIT_LOG_ASSET,
  FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS,
  FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE,
  FETCH_WAVEFORM_SUCCESS,
  FETCH_WAVEFORM_FAILURE,
  FETCH_CASE_MEDIA_SUCCESS,
  FETCH_CASE_MEDIA_FAILURE,
  FETCH_FOLDER_INFO_SUCCESS,
  FETCH_FOLDER_INFO_FAILURE,
  FETCH_MEDIA_COMMENTS_SUCCESS,
  FETCH_MEDIA_COMMENTS_FAILURE,
  UPDATE_COMMENT_SUCCESS,
  UPDATE_COMMENT_FAILURE,
  FETCH_MEDIA_COMMENTS_USER_SUCCESS,
  FETCH_MEDIA_COMMENTS_USER_FAILURE,
  FETCH_MEDIA_USER_SUCCESS,
  FETCH_MEDIA_USER_FAILURE,
  TDO_LOCK_FOUND_SUCCESS,
  TDO_LOCK_FOUND_FAILURE,
  UpdateTDOLockRequest,
  UpdateTDOLockResponse,
  SECURE_TDO_LOCK_SUCCESS,
  SECURE_TDO_LOCK_FAILURE,
  TDO_NAME_CHANGE_SUCCESS,
  TDO_NAME_CHANGE_FAILURE,
  FETCH_UPDATE_TDO_DETAILS_SUCCESS,
  FETCH_UPDATE_TDO_DETAILS_FAILURE,
  UPDATE_COMMENT,
  CREATE_ROTATE_VIDEO_JOB_SUCCESS,
  CREATE_ROTATE_VIDEO_JOB_FAILURE,
  UPDATE_TDO_PERMISSIONS,
  REDACTION_DATA_BURN_IN_LABELLED,
} from './actions';
import type { TaskStatus } from 'veritone-types';
import { DeepWritable, Head } from 'ts-essentials';
import { GetActionCreatorPayloadT, formatTDOName } from '@utils';
import { arrayHasLength } from '@common/shared/util';
import getLastRedactedFile from '@helpers/getLastRedactedFile';
import { saveAuditLogAsset } from '@helpers/saveAsset';
import {
  formatAuditEventsCsv,
  type AuditEventRecord,
} from '@common/utils/auditLogCsv';
import { Action } from '@reduxjs/toolkit';
import { selectFeatureFlags } from '@common/user-permissions/selectors';
import { generateTasksAndRoutes } from '@helpers/engineJobHelper';
import {
  DELETE_OBJECT_DETECTION_RESULTS_FAILURE,
  DELETE_OBJECT_DETECTION_RESULTS_SUCCESS,
} from '../facesTabModule';
import { SYSTEM_FOLDER_REDACT_TRASH } from '@redact-modules/trash/constants';
import {
  DELETE_SPEAKER_SEPARATION_RESULTS_FAILURE,
  DELETE_SPEAKER_SEPARATION_RESULTS_SUCCESS,
  DELETE_TRANSCRIPTION_RESULTS_FAILURE,
  DELETE_TRANSCRIPTION_RESULTS_SUCCESS,
} from './actions/transcription';

const {
  user: { selectUser },
} = modules;

export interface FETCH_TDO_QUERY_RESPONSE {
  temporalDataObject: {
    id: TDOId;
    name: string;
    status: 'downloaded' | 'recording' | 'recorded';
    foldersTreeObjectIds: FolderId[];
    folders?: {
      name?: string;
      folderPath: { name?: string }[];
    }[];
    thumbnailUrl: string;
    modifiedDateTime: string;
    startDateTime: string;
    stopDateTime: string;
    details: {
      name?: string;
      settings?: BaseGlobalSettings;
      tags?: ReadonlyArray<Tag>;
      isExport?: boolean;
    } & Record<string, any>;
    tasks: {
      records: {
        id: string;
        status: TaskStatus;
        createdDateTime: string;
        startedDateTime: string;
        completedDateTime: string;
        engineId: string;
        engine: {
          id: string;
          categoryId: string;
          name: string;
        };
        jobId: string;
        job: {
          status: string;
        };
        ioFolders: {
          referenceId: string;
        }[];
      }[];
    };
    primaryAsset: {
      readonly id: string;
      readonly name: string | null;
      readonly description: string | null;
      readonly signedUri: string;
      readonly details?: {
        readonly virtualAsset?: boolean;
      };
      readonly jsondata: {
        readonly mediaDuration?: number;
      };
      readonly contentType: string;
    };
    redactedMediaAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        modifiedDateTime: string;
        signedUri: string;
        details: Record<string, any>;
        fileData: {
          md5sum: string;
        };
      }[];
    };
    redactionDataAssets: {
      records: {
        id: string;
        createdDateTime: string;
        details: Record<string, any>;
        fileData?: { size?: number | null };
      }[];
    };
    redactionSelectionAssets: {
      records: {
        id: string;
        createdDateTime: string;
        details: Record<string, any>;
        fileData?: { size?: number | null };
      }[];
    };
    auditLogAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        createdDateTime: string;
        signedUri: string;
        details: Record<string, any>;
      }[];
    };
    redactExportAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        signedUri: string;
        details: Record<string, any>;
      }[];
    };
    waveformAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        modifiedDateTime: string;
        signedUri: string;
        details: Record<string, any>;
        fileData: {
          md5sum: string;
        };
      }[];
    };
    thumbnailAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        signedUri: string;
        details: Record<string, any>;
      }[];
    };
    transcriptAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        signedUri: string;
        details: Record<string, any>;
      }[];
    };
    streams: Array<{
      protocol: 'hls' | 'dash';
      uri: string;
    }>;
  };
}

export function isTdoInTrash(tdo: DetailTDO | null | undefined): boolean {
  const leafFolder = tdo?.folders?.[0];
  // VE-21114: under v2 the TDO's folderPath excludes its own (leaf) folder, so a
  // file sitting directly in Trash has "Redact Trash" as its leaf — absent from
  // folderPath. Check the leaf's own name too; folderPath still covers v1 and
  // the case where Trash is an ancestor.
  if (leafFolder?.name === SYSTEM_FOLDER_REDACT_TRASH) {
    return true;
  }
  const folderPath = leafFolder?.folderPath;
  return (
    !!folderPath &&
    folderPath.length > 0 &&
    folderPath.some((folder) => folder?.name === SYSTEM_FOLDER_REDACT_TRASH)
  );
}

interface TASKS_QUERY_RESPONSE {
  temporalDataObject: {
    tasks: FETCH_TDO_QUERY_RESPONSE['temporalDataObject']['tasks'];
  };
}

export function getFetchTdoSuccessPayload(tdoInfo: FETCH_TDO_QUERY_RESPONSE): {
  temporalDataObject: DetailTDO;
} {
  const settings = tdoInfo?.temporalDataObject?.details?.settings; // important type difference for type cleanup
  return {
    temporalDataObject: {
      ...tdoInfo.temporalDataObject,
      details: {
        ...tdoInfo.temporalDataObject.details,
        settings: settings
          ? getGlobalSettings(settings)
          : DEFAULT_GLOBAL_SETTINGS,
      },
    },
  };
}

export const fetchTdo =
  (id: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const limit = 300;

    const query = `
      query($id: ID!, $limit: Int) {
        temporalDataObject(id: $id) {
          id
          name
          status
          foldersTreeObjectIds
          folders {
            name
            folderPath {
              name
            }
          }
          thumbnailUrl
          modifiedDateTime
          startDateTime
          stopDateTime
          details
          tasks (limit: $limit) {
            records {
              id
              status
              #taskOutput
              createdDateTime
              startedDateTime
              completedDateTime
              engineId
              engine {
                id
                categoryId
                name
              }
              jobId
              job {
                status
              }
              ioFolders {
                referenceId
              }
            }
          }
          primaryAsset(assetType: "media") {
            id
            name
            description
            signedUri
            jsondata
            contentType
          }
          redactedMediaAssets: assets(assetType: ["redacted-media", "redacted-media-clip"]) {
            records {
              id
              name
              contentType
              # VE-25870: burn-in time must come from createdDateTime.
              # modifiedDateTime is bumped whenever the asset's metrics are
              # rewritten (a download increments the counter), which would move
              # the burn-in mark forward and hide a stale output.
              createdDateTime
              modifiedDateTime
              signedUri
              details
              fileData {
                md5sum
              }
            }
          }
          auditLogAssets: assets(orderBy: createdDateTime, orderDirection: desc, assetType: ["redact-audit-log", "blur-audit-log"]) {
            records {
              id
              name
              contentType
              # VE-27735: the reducer merges local records into this snapshot, so
              # it needs the timestamp to restore the newest-first order the
              # server returned rather than guessing from position.
              createdDateTime
              signedUri
              details
            }
          }
          # VE-25870: the newest save, whose id the Redacted Files tab compares
          # against the one the output was built from. Only the id is needed, so
          # only the newest record is fetched — never a body. fileData.size gates
          # the legacy timestamp fallback: a body big enough to take minutes to
          # upload makes the race tolerance meaningless, so those files are left
          # unchecked rather than warned about on a guess.
          redactionDataAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redaction-media-data"]) {
            records {
              id
              createdDateTime
              details
              fileData {
                size
              }
            }
          }
          # VE-25870: a redacted output names the user-selections asset it consumed
          # (details.redactionAssetId), and that asset's createdDateTime marks when
          # Redact File snapshotted. Two uses: the cutoff a settings change had to
          # beat to reach that burn-in, and the anchor for outputs too old to carry
          # a version label. Matched by that id rather than taken on trust — the
          # newest one can belong to a later run that failed or is still going.
          redactionSelectionAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redaction-user-selections"]) {
            records {
              id
              createdDateTime
              details
              fileData {
                size
              }
            }
          }
          redactExportAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redact-export", "redacted-audit-log"]) {
            records {
              id
              name
              contentType
              signedUri
              details
            }
          }
          waveformAssets: assets(assetType: "waveform-csv") {
            records {
              id
              name
              contentType
              modifiedDateTime
              signedUri
              details
              fileData {
                md5sum
              }
            }
          }
          thumbnailAssets: assets(assetType: "thumbnail-sprite") {
            records {
              id
              name
              contentType
              signedUri
              details
            }
          }
          transcriptAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: "redacted-transcript") {
            records {
              id
              name
              contentType
              signedUri
              details
            }
          }
          streams {
            protocol
            uri
          }
        }
      }
    `;

    const tdoInfo = await callGraphQLApi<FETCH_TDO_QUERY_RESPONSE>({
      actionTypes: [FETCH_TDO, '', FETCH_TDO_WITH_FAILURE],
      query,
      variables: { id, limit },
      dispatch,
      getState,
      bailout: undefined,
    });

    // check if need load additional tasks
    if (
      tdoInfo &&
      get(tdoInfo, 'temporalDataObject.tasks.records', []).length === limit
    ) {
      const taskQuery = `
      query($id: ID!, $offset: Int, $limit: Int) {
        temporalDataObject: temporalDataObject(id: $id) {
          tasks(limit: $limit, offset: $offset) {
            records {
              id
              status
              createdDateTime
              startedDateTime
              completedDateTime
              engineId
              engine {
                id
                categoryId
                name
              }
              jobId
              job {
                status
              }
              ioFolders {
                referenceId
              }
            }
          }
        }
      }
    `;

      let allTasks = tdoInfo.temporalDataObject.tasks?.records || [];

      let offset = 0;
      let taskCount = allTasks.length;
      // load more tasks
      while (taskCount === limit) {
        offset += limit;
        const result = await callGraphQLApi<TASKS_QUERY_RESPONSE>({
          actionTypes: ['', '', FETCH_TDO_WITH_FAILURE],
          query: taskQuery,
          variables: {
            id: id,
            limit: limit,
            offset: offset,
          },
          bailout: undefined,
          dispatch,
          getState,
        });

        // concatenate additional tasks
        allTasks = allTasks.concat(
          result?.temporalDataObject?.tasks?.records || []
        );
        taskCount = result?.temporalDataObject?.tasks?.records?.length || 0;
      }
      if (tdoInfo?.temporalDataObject?.tasks) {
        tdoInfo.temporalDataObject.tasks.records = allTasks;
      } else {
        tdoInfo.temporalDataObject.tasks = {
          records: allTasks,
        };
      }
    }

    if (tdoInfo) {
      dispatch(FETCH_TDO_SUCCESS(getFetchTdoSuccessPayload(tdoInfo)));
    }

    return tdoInfo;
  };

export const queryTdoModified =
  (tdoId: TDOId) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const query = `
      query queryTdoModified($id: ID!) {
        temporalDataObject(id: $id) {
          id
          modifiedDateTime
          # assets(assetType: "redaction-media-data", orderBy: createdDateTime, limit: 1) {
          #  records {
          #    createdDateTime
          #    details
          #  }
          #}
        }
      }
    `;
    const resp = await callGraphQLApi<{
      temporalDataObject?: {
        id: TDOId;
        modifiedDateTime: string;
        assets?: {
          records: {
            jsonData: Record<string, any>;
            createdDateTime: string;
            details: Record<string, any>;
          }[];
        };
      };
    }>({
      actionTypes: ['', '', ''], // TDO_EXTERNALLY_MODIFIED_FAILED'],
      query,
      variables: { id: tdoId },
      dispatch,
      getState,
      bailout: undefined,
    });

    const currentTdo = local(getState()).tdo;

    const currentModifiedDateTime = currentTdo?.modifiedDateTime;
    const modifiedDateTime = resp?.temporalDataObject?.modifiedDateTime;

    if (modifiedDateTime && currentModifiedDateTime !== modifiedDateTime) {
      dispatch(Actions.TDO_EXTERNALLY_MODIFIED());
    }
    return resp;
  };

export interface REFRESH_TDO_TASKS_QUERY_RESPONSE {
  temporalDataObject: {
    id: TDOId;
    tasks: {
      records: {
        id: string;
        status: TaskStatus;
        createdDateTime: string;
        startedDateTime: string;
        completedDateTime: string;
        engineId: string;
        engine: {
          id: string;
          categoryId: string;
          name: string;
        };
        jobId: string;
        job: {
          status: string;
        };
        ioFolders: {
          referenceId: string;
        }[];
      }[];
    };
  };
}

export const refreshTdoTasks =
  (id: string, limit: number) =>
  async (
    dispatch: Head<
      Parameters<typeof callGraphQLApi<REFRESH_TDO_TASKS_QUERY_RESPONSE>>
    >['dispatch'],
    getState: Head<
      Parameters<typeof callGraphQLApi<REFRESH_TDO_TASKS_QUERY_RESPONSE>>
    >['getState']
  ) => {
    const query = `
      query($id: ID!, $limit: Int) {
        temporalDataObject(id: $id) {
          id
          tasks (limit: $limit) {
            records {
              id
              status
              #taskOutput
              createdDateTime
              startedDateTime
              completedDateTime
              engineId
              engine {
                id
                categoryId
                name
              }
              jobId
              job {
                status
              }
              ioFolders {
                referenceId
              }
            }
          }
        }
      }
    `;

    return await callGraphQLApi<REFRESH_TDO_TASKS_QUERY_RESPONSE>({
      actionTypes: [
        REFRESH_TDO_TASKS,
        REFRESH_TDO_TASKS_SUCCESS,
        REFRESH_TDO_TASKS_FAILURE,
      ],
      query,
      variables: { id, limit },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface RefreshTdoPrimaryAssetResponse {
  temporalDataObject: {
    id: string;
    primaryAsset: {
      id: string;
    };
  };
}
export const refreshTdoPrimaryAsset =
  (id: string) =>
  async (
    dispatch: GQLApiDispatch<RefreshTdoPrimaryAssetResponse>,
    getState: GQLApiGetState
  ) => {
    const query = `
      query($id: ID!) {
        temporalDataObject(id: $id) {
          id
          primaryAsset(assetType: "media") {
            id
          }
        }
      }
    `;

    return await callGraphQLApi<RefreshTdoPrimaryAssetResponse>({
      actionTypes: [
        REFRESH_TDO_PRIMARY_ASSET_ID,
        REFRESH_TDO_PRIMARY_ASSET_ID_SUCCESS,
        REFRESH_TDO_PRIMARY_ASSET_ID_FAILURE,
      ],
      query,
      variables: { id },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export const fetchWaveformCsv =
  (signedUri: string) => async (dispatch: (action: Action) => void) => {
    try {
      const respText = await fetch(signedUri, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/csv',
        },
      }).then((resp) => resp.text());

      const respRows = respText.split('\n').filter((row) => row);
      const metadataRow = respRows.shift()?.split(',');
      if (arrayHasLength(metadataRow, 4)) {
        const respMetadata = {
          stepSize: parseFloat(metadataRow[2].split('=')[1] || ''),
          scale: parseFloat(metadataRow[3].split('=')[1] || ''),
        };

        if (
          isFinite(respMetadata.stepSize) &&
          isFinite(respMetadata.scale) &&
          respMetadata.scale !== 0 &&
          respMetadata.stepSize !== 0
        ) {
          const waveforms = respRows.map(
            (row: string, i: number): AudiowaveFrame => {
              const rowNumbers = row
                .split(',')
                .map((_string) => parseFloat(_string));

              return {
                startTime: i * respMetadata.stepSize,
                stopTime: (i + 1) * respMetadata.stepSize,
                minValue: (rowNumbers[0] || 0) / respMetadata.scale,
                maxValue: (rowNumbers[1] || 0) / respMetadata.scale,
              };
            }
          );

          dispatch(FETCH_WAVEFORM_SUCCESS(waveforms));
          return;
        }
      }
      throw new Error('Malformed waveform CSV');
    } catch (err) {
      dispatch(
        FETCH_WAVEFORM_FAILURE(
          err instanceof Error ? err.message : 'Unknown error'
        )
      );
    }
  };

export type DeleteLastAssetResponse = Record<
  string,
  {
    id: string;
  }
>;
export const deleteLastAsset =
  (action: { payload: { id?: string; ids?: string[] } }) =>
  async (
    dispatch: Head<Parameters<typeof callGraphQLApi>>['dispatch'],
    getState: Head<Parameters<typeof callGraphQLApi>>['getState']
  ) => {
    const id = action.payload?.id;
    const ids = action.payload?.ids;
    if (!id && !ids) {
      return;
    }

    const query = id
      ? `
      mutation ($id: ID!){
        deleteAsset(id: $id) {
          id
        }
      }
    `
      : `mutation deleteMultipleAssets{
        ${ids! // Safe due to prior check
          .map(
            (
              id: string,
              ind: number
            ) => `deleteAsset${ind}: deleteAsset(id: "${id}") {
              id
        }`
          )
          .join(',')}
      }`;

    return await callGraphQLApi<DeleteLastAssetResponse>({
      actionTypes: [
        FETCH_DELETE_LAST_ASSET,
        FETCH_DELETE_LAST_ASSET_SUCCESS,
        FETCH_DELETE_LAST_ASSET_FAILURE,
      ],
      query,
      variables: id ? { id } : {},
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface UpdateSettingsOnDetailsResponse {
  updateTDO: {
    id: TDOId;
    details: Record<string, any>;
    modifiedDateTime: string;
  };
}
/**
 * The two fields the settings writer owns, stamped for a genuine user change.
 *
 * VE-30516: `isSaved` and `lastSavedDateTime` are added here on the way to the
 * server and are deliberately NOT mirrored into redux by the
 * `SET_GLOBAL_SETTINGS` reducer, which only reflects the action payload. Shared
 * so the two callers that persist a user-made settings change — the settings
 * writer and undo/redo — cannot drift on what "a change" writes. The seed is NOT
 * a caller: a seed is not a change, so it persists `isSaved` without a stamp.
 */
export const settingsAsUserChange = (settings: Record<string, any>) => ({
  ...settings,
  isSaved: true,
  lastSavedDateTime: new Date().toISOString(),
});

export const updateSettingsOnDetails =
  (tdoId: TDOId, settings: Record<string, any>) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const details = {
      settings,
    };

    const query = `
      mutation updateTDO($tdoId: ID!, $details: JSONData){
        updateTDO( input: {
          id: $tdoId
          details: $details
        })
        {
          id
          details
          modifiedDateTime
        }
      }
    `;

    return await callGraphQLApi<UpdateSettingsOnDetailsResponse>({
      actionTypes: [
        FETCH_UPDATE_SETTINGS,
        FETCH_UPDATE_SETTINGS_SUCCESS,
        FETCH_UPDATE_SETTINGS_FAILURE,
      ],
      query,
      variables: { tdoId, details },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface UpdateNameResponse {
  updateTDO: {
    id: TDOId;
    name: string;
    details: DeepWritable<DetailTDO['details']>;
    modifiedDateTime: string;
  };
}

export const updateNameOnDetails =
  (nameUpdate: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    // TODO: Fix getState typing
    const state = getState() as Parameters<typeof local>[0];
    const tdo = local(state).tdo;
    const tdoId = tdo?.id;
    if (!tdoId) {
      return;
    }

    const name = formatTDOName(nameUpdate);

    const query = `
      mutation updateNameTDO($tdoId: ID!, $details: JSONData, $name: String){
        updateTDO( input: {
          id: $tdoId,
          name: $name,
          details: $details
        })
        {
          id
          name
          details
          modifiedDateTime
        }
      }
    `;

    return await callGraphQLApi<UpdateNameResponse>({
      actionTypes: ['NOOP', TDO_NAME_CHANGE_SUCCESS, TDO_NAME_CHANGE_FAILURE],
      query,
      variables: {
        tdoId,
        name,
        details: {
          name,
          // `veritoneFile` is its own row and only this writer changes two of
          // its fields, so it is the one key here that genuinely needs the
          // read-modify-write.
          veritoneFile: {
            ...tdo.details.veritoneFile,
            fileName: name,
            filename: name,
          },
        },
      },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface UpdateStatusResponse {
  updateTDO: {
    id: TDOId;
    details: DetailTDO['details'];
    modifiedDateTime: string;
  };
}
/**
 * `updateTDO` merges `details` at the TOP level only: each key it is given
 * replaces the server's row for that key wholesale, and a key it is NOT given is
 * left untouched (aiware-core `dal/tdo.js` emits one DELETE+INSERT per supplied
 * key). So the rule for every writer below is **send only the keys you own**.
 *
 * VE-30516: these writers used to spread the whole of redux's `tdo.details`,
 * which resent `settings` as redux knew it — and redux never learns `isSaved` or
 * `lastSavedDateTime`, because the settings writer adds them on its way out. A
 * SAVE therefore deleted the timestamp the stale-redacted-file check judges by.
 *
 * The first fix was a helper that stripped `settings`. That protected one field
 * and nothing else: the next `details` key some other process owns would be
 * clobbered by the identical mechanism. Naming what each writer owns is immune
 * to the whole class, and keeps these payloads under the server's
 * `maxMetadataSize` guard.
 *
 * Mind what counts as owned. `redact.trimInterval` is mutated in redux by the
 * trim controls and has NO writer of its own — the save path is the only thing
 * that persists it — so `updateStatusOnDetails` owns `redact` as well as `tags`.
 * Dropping it would silently stop trims being saved.
 */

export const updateStatusOnDetails =
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    // TODO: Type state properly
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();
    const tdo = local(state).tdo;
    const tdoId = tdo?.id;
    if (!tdoId) {
      return;
    }

    const query = `
      mutation updateTDO($tdoId: ID!, $details: JSONData) {
        updateTDO(input: {
          id: $tdoId
          details: $details
        })
        {
          id
          details
          modifiedDateTime
        }
      }
    `;

    let detailTags = tdo.details?.tags;
    // handle case with empty tags
    if (!detailTags?.length) {
      detailTags = [
        {
          value: IN_REDACTION_TAG_KEY,
          redactionStatus: DEFAULT_REDACTION_STATUS_LABEL,
        },
      ];
    }

    return await callGraphQLApi<UpdateStatusResponse>({
      actionTypes: [
        FETCH_STATUS_CHANGE,
        FETCH_STATUS_CHANGE_SUCCESS,
        FETCH_STATUS_CHANGE_FAILURE,
      ],
      query,
      variables: {
        tdoId: tdoId,
        details: {
          tags: detailTags,
          // Present even when empty: clearing a trim leaves `redact` without
          // `trimInterval`, and that removal has to reach the server too.
          ...(tdo.details.redact !== undefined && {
            redact: tdo.details.redact,
          }),
        },
      },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export const updateRedactionStatusOnDetails =
  (fields: RedactionStatusTagWriteFields) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const state = getState() as Parameters<typeof local>[0];
    const tdo = local(state).tdo;
    const tdoId = tdo?.id;
    if (!tdoId) {
      return;
    }

    const query = `
      mutation updateTDO($tdoId: ID!, $details: JSONData) {
        updateTDO(input: {
          id: $tdoId
          details: $details
        })
        {
          id
          details
          modifiedDateTime
        }
      }
    `;

    const originalTags = tdo.details?.tags || [];
    const tagIndex = originalTags.findIndex(
      (tag) => tag?.value === IN_REDACTION_TAG_KEY
    );

    let detailTags: Array<Tag | null>;
    if (tagIndex > -1) {
      detailTags = originalTags.map((tag, index) =>
        index === tagIndex && tag
          ? mergeRedactionStatusTagFields(
              { ...tag, value: IN_REDACTION_TAG_KEY },
              fields
            )
          : tag
      );
    } else {
      detailTags = [
        ...originalTags,
        mergeRedactionStatusTagFields({ value: IN_REDACTION_TAG_KEY }, fields),
      ];
    }

    return await callGraphQLApi<UpdateStatusResponse>({
      actionTypes: [
        'NOOP',
        FETCH_STATUS_CHANGE_SUCCESS,
        FETCH_STATUS_CHANGE_FAILURE,
      ],
      query,
      variables: {
        tdoId,
        details: { tags: detailTags },
      },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

// Persist the latest redacted-media version onto the TDO's details so a future
// image redact can resume version numbering even if every prior redacted asset
// is manually deleted. The mutation returns details so the success reducer can
// merge them into state.tdo without requiring a TDO refetch.
export const updateLatestRedactedVersionOnDetails =
  (version: number) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const state = getState() as Parameters<typeof local>[0];
    const tdo = local(state).tdo;
    const tdoId = tdo?.id;
    if (!tdoId) {
      return;
    }

    const query = `
      mutation updateTDODetails($id: ID!, $details: JSONData) {
        updateTDO(input: { id: $id, details: $details }) {
          id
          details
          modifiedDateTime
        }
      }
    `;

    return await callGraphQLApi<UpdateStatusResponse>({
      actionTypes: [
        'NOOP',
        FETCH_UPDATE_TDO_DETAILS_SUCCESS,
        FETCH_UPDATE_TDO_DETAILS_FAILURE,
      ],
      query,
      variables: {
        id: tdoId,
        details: { latestRedactedMediaVersion: version },
      },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface CreatedAuditLogAsset {
  id: string;
  name: string;
  contentType: string;
  signedUri: string;
  details: Record<string, any>;
}

// Plain async function (not a thunk) so the saga can call it with `yield* call`
// and be guaranteed the Promise is awaited before continuing. Returns the
// created asset (or null on failure) — the caller dispatches
// ADD_AUDIT_LOG_ASSET_TO_TDO after the call completes so it is synchronously
// in state before SELECT_REDACTED_FILES_TAB fires.
export async function uploadImageAuditLog(
  tdoId: string,
  redactedMediaAssetId: string,
  version: number,
  redactedMediaFileName: string,
  getState: GQLApiGetState
): Promise<CreatedAuditLogAsset | null> {
  try {
    const auditEventsQuery = `
      query auditEvents($id: ID!, $application: String, $offset: Int, $limit: Int) {
        auditEvents(application: $application, terms: [{ tdoId: $id }], offset: $offset, limit: $limit) {
          count
          records {
            id
            payload
            application
          }
        }
      }
    `;

    const limit = 100;
    let offset = 0;
    const allRecords: AuditEventRecord[] = [];
    for (;;) {
      const resp = await callGraphQLApi<{
        auditEvents: {
          count: number;
          records: AuditEventRecord[];
        };
      }>({
        actionTypes: ['NOOP', 'NOOP', 'NOOP'],
        query: auditEventsQuery,
        variables: {
          id: tdoId,
          application: 'redact',
          offset,
          limit,
        },
        // callGraphQLApi calls dispatch unconditionally even though the param is
        // typed optional. Action types are all NOOP so the dispatches are
        // side-effect-free; a no-op satisfies the runtime requirement.

        dispatch: ((_action: any) => {}) as GQLApiDispatch,
        getState,
        throwOnError: true,
      });

      const records = resp?.auditEvents?.records ?? [];
      allRecords.push(...records);
      if (records.length < limit) {
        break;
      }
      offset += limit;
    }

    const csv = formatAuditEventsCsv(allRecords);
    const fileName = `${redactedMediaFileName}.csv`;

    const result: any = await saveAuditLogAsset(
      { tdoId, name: fileName, redactedMediaAssetId, version },
      csv,
      getState
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const created = result?.data?.createAsset;
    if (!created?.id) {
      return null;
    }

    return created as CreatedAuditLogAsset;
  } catch (error) {
    console.error(
      '[uploadImageAuditLog] Failed to create audit log asset:',
      error
    );
    return null;
  }
}

// Best-effort delete of a single asset by id. Errors are logged but not
// re-thrown so the caller can iterate through multiple cleanup targets without
// one bad id aborting the rest.
export const deleteAssetById =
  (assetId: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const query = `
      mutation deleteAsset($id: ID!) {
        deleteAsset(id: $id) {
          id
          message
        }
      }
    `;

    try {
      return await callGraphQLApi<{
        deleteAsset: { id: string; message: string };
      }>({
        actionTypes: ['NOOP', 'NOOP', 'NOOP'],
        query,
        variables: { id: assetId },
        dispatch,
        getState,
        throwOnError: true,
      });
    } catch (error) {
      console.error(
        `[deleteAssetById] Failed to delete asset ${assetId}:`,
        error
      );
      return null;
    }
  };

export interface SendEventResponse {
  emitAuditEvent: {
    id: string;
  };
}
export const sendEvent =
  (_schemaId: string, triggerAction: { payload: string }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    // TODO: Type state properly
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();
    const tdo = local(state).tdo;
    const tdoId = tdo?.id;
    if (!tdo) {
      return;
    }
    const md5sum = get(getLastRedactedFile(tdo), 'fileData.md5sum');
    const userName = selectUser(state).userName;
    const action = triggerAction.payload;
    const payload = {
      action,
      tdoId,
      md5sum,
      userName,
      timestamp: new Date().toISOString(),
    };
    const query = `mutation ($payload: JSONData!, $application: String){
      emitAuditEvent (input: {
        application: $application
        payload: $payload
      }) {
        id
      }
    }
  `;

    return await callGraphQLApi<SendEventResponse>({
      actionTypes: [
        LOG_AUDIT_EVENT_INIT,
        LOG_AUDIT_EVENT_SUCCESS,
        LOG_AUDIT_EVENT_FAILURE,
      ],
      query,
      variables: { payload, application: 'redact' },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface PollToDownloadResponse {
  temporalDataObject: {
    assets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        modifiedDateTime: string;
        assetType: string;
        signedUri: string;
        details: Record<string, any>;
        sourceData: {
          taskId: string;
          sourceId: string;
        };
      }[];
    };
  };
}
export const pollToDownload =
  (tdoId: TDOId) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const state = getState() as Parameters<typeof local>[0];
    const id = local(state)?.tdo?.id || tdoId;
    const query = `
      query ($id: ID!){
      temporalDataObject(id: $id) {
          assets(assetType: ["redact-export", "redacted-audit-log"]) {
            records {
              id
              name
              contentType
              modifiedDateTime
              assetType
              signedUri
              details
              sourceData{
                taskId
                sourceId
              }
            }
          }
        }
      }
    `;

    return await callGraphQLApi<PollToDownloadResponse>({
      actionTypes: [
        POLL_DOWNLOAD_ASSET,
        POLL_DOWNLOAD_ASSET_SUCCESS,
        POLL_DOWNLOAD_ASSET_FAILURE,
      ],
      query,
      variables: { id },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface RetrieveTdoIdFromJobIdResponse {
  job: {
    targetId: TDOId;
    tasks: {
      records: {
        engineId: string;
      }[];
    };
  };
}
export const retrieveTdoIdFromJobId =
  (id: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const query = `
      query ($id: ID!) {
        job(id: $id) {
          targetId
          tasks {
            records {
              engineId
              #taskOutput
            }
          }
        }
      }
    `;

    return await callGraphQLApi<RetrieveTdoIdFromJobIdResponse>({
      actionTypes: [
        RETRIEVE_TDO_FROM_JOB,
        RETRIEVE_TDO_FROM_JOB_SUCCESS,
        RETRIEVE_TDO_FROM_JOB_FAILURE,
      ],
      query,
      variables: { id },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface UpdateLastRedactedFileMetricsResponse {
  updateAsset: {
    id: string;
  };
}
export const updateLastRedactedFileMetrics =
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const state = getState() as Parameters<typeof local>[0];
    const tdo = local(state).tdo;
    if (!tdo) {
      return;
    }
    const lastRedacted = getLastRedactedFile(tdo);
    const query = `
      mutation ($id: ID!, $details: JSONData!){
        updateAsset(input: {
          id: $id,
          details: $details
        }) {
          id
        }
      }
    `;

    return await callGraphQLApi<UpdateLastRedactedFileMetricsResponse>({
      actionTypes: [
        FETCH_UPDATE_REDACTED_FILE_METRICS,
        FETCH_UPDATE_REDACTED_FILE_METRICS_SUCCESS,
        FETCH_UPDATE_REDACTED_FILE_METRICS_FAILURE,
      ],
      query,
      variables: { id: lastRedacted?.id, details: lastRedacted?.details },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

/**
 * VE-25870: label a `redaction-media-data` asset with the redacted version the
 * burn-in about to be sent will produce.
 *
 * That number is `assetVersion` from the engine payload, so the redaction data
 * and the output it produces end up carrying the same value by agreement. The
 * staleness check then asks one question — does the newest save's label match
 * the newest output's version — with no dependence on which asset was written
 * first, or on one side having observed an id from the other.
 *
 * `burnInRequested` is written on every save (false by default, see
 * `saveMediaData`) so its three states stay distinguishable: true means this
 * data was sent for burn-in, false means it was an ordinary save that never was,
 * and the key being absent means the asset predates the scheme entirely and only
 * the timestamp comparison can judge it.
 *
 * `updateAsset` replaces `details` wholesale rather than merging, so the asset's
 * existing details are spread back in. They come from the record the TDO fetch
 * (or the save that just landed) put in state.
 */
export const markRedactionDataBurnedIn =
  (assetId: string | undefined, expectedRedactedVersion: number) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    if (!assetId) {
      return;
    }
    const state = getState() as Parameters<typeof local>[0];
    const record = local(state).tdo?.redactionDataAssets?.records?.find(
      (asset) => asset.id === assetId
    );
    const query = `
      mutation ($id: ID!, $details: JSONData!){
        updateAsset(input: { id: $id, details: $details }) {
          id
        }
      }
    `;

    const result = await callGraphQLApi<{ updateAsset: { id: string } }>({
      actionTypes: ['NOOP', 'NOOP', 'NOOP'],
      query,
      variables: {
        id: assetId,
        details: {
          ...(record?.details ?? {}),
          burnInRequested: true,
          expectedRedactedVersion,
        },
      },
      dispatch,
      getState,
      bailout: undefined,
    });

    // Mirror it locally. Nothing fetches `redactionDataAssets` again after a burn-in
    // — `fetchRedactedMedia` covers the outputs and the selections asset only —
    // so without this the tab reads the save as never-burned-in and keeps warning
    // until the page is reloaded.
    dispatch(
      REDACTION_DATA_BURN_IN_LABELLED({ assetId, expectedRedactedVersion })
    );
    return result;
  };

export interface FecthDownloadAssetStatusResponse {
  job: {
    status: string;
    createdDateTime: string;
  };
}
export const fetchDownloadAssetStatus =
  (payload: { jobId: string }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const jobId = payload.jobId;

    const query = `query($jobId: ID!) {
      job(id: $jobId) {
        status
        createdDateTime
      }
    }`;

    return await callGraphQLApi<FecthDownloadAssetStatusResponse>({
      actionTypes: [
        DOWNLOAD_ASSET_JOB_STATUS,
        DOWNLOAD_ASSET_JOB_STATUS_SUCCESS,
        DOWNLOAD_ASSET_JOB_STATUS_FAILURE,
      ],
      query,
      variables: { jobId },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface FetchRedactionJobStatusResponse {
  job: {
    status: string;
    createdDateTime: string;
  };
}
// Update MDP after Redaction job completes
export const fetchRedactionJobStatus =
  (payload: { jobId: string }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const jobId = payload.jobId;

    const query = `query($jobId: ID!) {
      job(id: $jobId) {
        status
        createdDateTime
      }
    }`;

    return await callGraphQLApi<FetchRedactionJobStatusResponse>({
      actionTypes: [
        REDACTION_JOB_STATUS,
        REDACTION_JOB_STATUS_SUCCESS,
        REDACTION_JOB_STATUS_FAILURE,
      ],
      query,
      variables: { jobId },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface FetchRedactedMediaResponse {
  temporalDataObject: {
    redactedMediaAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        modifiedDateTime: string;
        signedUri: string;
        details: Record<string, any>;
        fileData: {
          md5sum: string;
        };
      }[];
    };
    auditLogAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        createdDateTime: string;
        signedUri: string;
        details: Record<string, any>;
      }[];
    };
    redactExportAssets: {
      records: {
        id: string;
        name: string;
        contentType: string;
        signedUri: string;
        details: Record<string, any>;
      }[];
    };
    /** VE-25870: carries the redaction data each burn-in consumed. */
    redactionSelectionAssets?: {
      records: {
        id: string;
        createdDateTime: string;
        details: Record<string, any>;
        fileData?: { size?: number | null };
      }[];
    };
  };
}
export const fetchRedactedMedia =
  (_id: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const state = getState() as Parameters<typeof local>[0];
    const id = local(state).tdo?.id; // TypeError when back to homepage: Cannot read property 'id' of null
    const query = `
      query($id: ID!) {
        temporalDataObject(id: $id) {
          redactedMediaAssets: assets(assetType: ["redacted-media", "redacted-media-clip"]) {
            records {
              id
              name
              contentType
              # VE-25870: burn-in time must come from createdDateTime.
              # modifiedDateTime is bumped whenever the asset's metrics are
              # rewritten (a download increments the counter), which would move
              # the burn-in mark forward and hide a stale output.
              createdDateTime
              modifiedDateTime
              signedUri
              details
              fileData {
                md5sum
              }
            }
          }
          auditLogAssets: assets(orderBy: createdDateTime, orderDirection: desc, assetType: ["redact-audit-log", "blur-audit-log"]) {
            records {
              id
              name
              contentType
              # VE-27735: the reducer merges local records into this snapshot, so
              # it needs the timestamp to restore the newest-first order the
              # server returned rather than guessing from position.
              createdDateTime
              signedUri
              details
            }
          }
          redactExportAssets: assets(assetType: ["redact-export", "redacted-audit-log"]) {
            records {
              id
              name
              contentType
              signedUri
              details
            }
          }
          # VE-25870: the burn-in just written points at the
          # redaction-user-selections asset it consumed, and only that asset
          # names the redaction data behind it. Redact File creates that asset
          # immediately before sending the job, so it never exists at page load —
          # the list the page fetched cannot contain the asset any in-session
          # burn-in refers to. Without refetching, a settings change made during
          # this session is dated against the previous burn-in.
          redactionSelectionAssets: assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redaction-user-selections"]) {
            records {
              id
              createdDateTime
              details
              fileData {
                size
              }
            }
          }
        }
      }
    `;

    return await callGraphQLApi<FetchRedactedMediaResponse>({
      actionTypes: [
        FETCH_REDACTED_MEDIA,
        FETCH_REDACTED_MEDIA_SUCCESS,
        FETCH_REDACTED_MEDIA_FAILURE,
      ],
      query,
      variables: { id },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface FetchLatestTDOAuditLogAssetResponse {
  temporalDataObject: {
    assets: {
      records: {
        id: string;
        name: string;
        assetType: string;
        contentType: string;
        signedUri: string;
      }[];
    };
  };
}

export const fetchLatestTDOAuditLogAsset =
  (tdoId: TDOId) =>
  async (
    dispatch: GQLApiDispatch<FetchLatestTDOAuditLogAssetResponse>,
    getState: GQLApiGetState
  ) => {
    const query = `
      query fetchLatestAuditLogAsset {
        temporalDataObject(id: "${tdoId}") {
          assets(limit: 1, orderBy: createdDateTime, orderDirection: desc, assetType: ["redact-audit-log", "blur-audit-log"]) {
            records {
              id
              name
              assetType
              contentType
              signedUri
            }
          }
        }
      }
    `;

    return await callGraphQLApi<FetchLatestTDOAuditLogAssetResponse>({
      actionTypes: [
        FETCH_LATEST_TDO_AUDIT_LOG_ASSET,
        FETCH_LATEST_TDO_AUDIT_LOG_ASSET_SUCCESS,
        FETCH_LATEST_TDO_AUDIT_LOG_ASSET_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export interface FetchCaseMediaResponse {
  folder: {
    id: string;
    name: string;
    childTDOs: {
      records: {
        id: TDOId;
        name: string;
        status: string;
        createdDateTime: string;
        modifiedDateTime: string;
        details: Record<string, any>;
        primaryAsset: {
          id: string;
          signedUri: string;
          contentType: string;
        };
        jobs: {
          records: {
            id: string;
            createdDateTime: string;
            modifiedDateTime: string;
            status: string;
            tasks: {
              records: {
                status: string;
                engineId: string;
              }[];
            };
          }[];
        };
        redactedMediaAssets: {
          records: {
            id: string;
            name: string;
            contentType: string;
            signedUri: string;
          }[];
        };
        streams: {
          protocol: string;
          uri: string;
        }[];
      }[];
    };
  };
}
export const fetchCaseMedia: Thunk<{
  readonly caseId: CaseId;
}> =
  ({ caseId }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const query = `
    query fetchCaseMedia($caseId: ID!) {
      folder(id: $caseId) {
      id
      name
        childTDOs(offset: 0, limit: 1000) {
          records {
            id
            name
            status
            details
            createdDateTime
            modifiedDateTime
            primaryAsset(assetType: "media") {
              id
              signedUri
              contentType
            }
            jobs(offset: 0, limit: 1) {
              records {
                id
                createdDateTime
                modifiedDateTime
                status
                tasks {
                  records {
                    status
                    engineId
                  }
                }
              }
            }
            redactedMediaAssets: assets(
              limit: 1
              orderBy: createdDateTime
              orderDirection: desc
              assetType: [
                "redacted-media"
                "redacted-media-clip"
                "redacted-document"
              ]
            ) {
              records {
                id
                name
                contentType
                signedUri
              }
            }
            streams {
              protocol
              uri
            }
          }
        }
      }
    }`;

    return await callGraphQLApi<FetchCaseMediaResponse>({
      actionTypes: ['', FETCH_CASE_MEDIA_SUCCESS, FETCH_CASE_MEDIA_FAILURE],
      query,
      variables: { caseId },
      dispatch,
      getState,
    });
  };

// Slim folder identity fetch — reuses fetchFolderQuery (with the added `name`
// field). Used by the external-integration "send file" saga so we don't have
// to pull the full fetchCaseMedia payload just for `caseName`.
export interface FetchFolderInfoResponse {
  folder: {
    id: string;
    name: string;
  };
}

export const fetchFolderInfo: Thunk<{ readonly folderId: CaseId }> =
  ({ folderId }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) =>
    await callGraphQLApi<FetchFolderInfoResponse>({
      actionTypes: ['', FETCH_FOLDER_INFO_SUCCESS, FETCH_FOLDER_INFO_FAILURE],
      query: fetchFolderQuery,
      variables: { folderId },
      dispatch,
      getState,
    });

export interface FetchMediaCommentsResponse {
  structuredDataObjects: {
    offset: number;
    records: Array<{ data: MediaComment }>;
  };
}

export const fetchMediaComments: Thunk<{
  readonly tdoId: TDOId;
  readonly limit: number;
  readonly offset: number;
}> =
  ({ tdoId, limit, offset }) =>
  async (
    dispatch: GQLApiDispatch<FetchMediaCommentsResponse>,
    getState: GQLApiGetState
  ) => {
    const schemaId = await lookupLatestMediaCommentsSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }

    const query = `
      query fetchComments($schemaId: ID!, $limit: Int!, $offset: Int!, $tdoId: ID!) {
        structuredDataObjects(
          limit: $limit
          offset: $offset
          schemaId: $schemaId
          filter: {
            tdoId: $tdoId
          }
        ) {
          offset
          records {
            data
          }
        }
      }
      `;

    return await callGraphQLApi<FetchMediaCommentsResponse>({
      actionTypes: [
        '',
        FETCH_MEDIA_COMMENTS_SUCCESS,
        FETCH_MEDIA_COMMENTS_FAILURE,
      ],
      query,
      variables: { schemaId, limit, offset, tdoId },
      dispatch,
      getState,
    });
  };

export const updateComment: Thunk<
  GetActionCreatorPayloadT<typeof UPDATE_COMMENT>
> =
  ({ comment, modify = true, isUndo }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const currentTime = new Date().toISOString();
    const { commentId: sdoId, createdDateTime } = comment;
    const schemaId = await lookupLatestMediaCommentsSchemaId(
      dispatch,
      getState
    );

    if (!schemaId) {
      return;
    }

    const updatedComment = {
      ...comment,
      createdDateTime: createdDateTime || currentTime,
      modifiedDateTime:
        modify && !isUndo ? currentTime : comment.modifiedDateTime,
      done: true,
      mediaTimestamp: Math.trunc(comment.mediaTimestamp),
    };
    const query = `
      mutation addComment($schemaId: ID!, $sdoId: ID!, $comment: JSONData!) {
        createStructuredData(input: {
          id: $sdoId
          schemaId: $schemaId
          data: $comment
        }) {
          data
        }
      }
      `;

    return await callGraphQLApi<{
      createStructuredData: { data: MediaComment };
    }>({
      actionTypes: ['', UPDATE_COMMENT_SUCCESS, UPDATE_COMMENT_FAILURE],
      query,
      variables: { schemaId, sdoId, comment: updatedComment },
      dispatch: (a) => dispatch({ ...a, payload: { ...a.payload, isUndo } }),
      getState,
    });
  };

export interface FetchMediaCommentsUsersResponse {
  basicUserInfo: {
    id: string;
    imageUrl: string | null;
    firstName: string;
    lastName: string;
  };
}

export const fetchMediaCommentsUser: Thunk<{
  readonly userId: string;
}> =
  ({ userId }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const query = `
      query getUser($userId: ID!) {
        basicUserInfo(id: $userId) {
          id
          imageUrl
          firstName
          lastName
        }
      }
      `;

    return await callGraphQLApi<FetchMediaCommentsUsersResponse>({
      actionTypes: [
        '',
        FETCH_MEDIA_COMMENTS_USER_SUCCESS,
        FETCH_MEDIA_COMMENTS_USER_FAILURE,
      ],
      query,
      variables: { userId },
      dispatch,
      getState,
    });
  };

export interface FetchMediaUserResponse {
  me: {
    id: string;
    firstName?: string;
    lastName?: string;
    imageUrl?: string | null;
    email?: string;
  };
}
export const fetchMediaUser =
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const query = `
      query getMe {
        me {
          id
          firstName
          lastName
          imageUrl
          email
        }
      }
    `;
    return await callGraphQLApi<FetchMediaUserResponse>({
      actionTypes: ['', FETCH_MEDIA_USER_SUCCESS, FETCH_MEDIA_USER_FAILURE],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

export const deleteAllHeadDetectionResults =
  ({ onlyUserEdited }: { onlyUserEdited?: boolean }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    try {
      const { detectionEngineCPU, detectionEngineGPU } =
        selectConfigEngines(getState());

      const detectionEngineIds = [detectionEngineCPU, detectionEngineGPU];

      const tdo = selectTdo(getState());

      const assetQuery = `
        query getAssets($id: ID!) {
          temporalDataObject(id: $id) {
            assets(assetType: "vtn-standard") {
              records {
                id
                jsondata
                isUserEdited
              }
            }
          }
        }
      `;
      interface AssetQueryResponse {
        temporalDataObject: {
          assets: {
            records: {
              id: string;
              jsondata: any;
              isUserEdited: boolean | null;
            }[];
          };
        };
      }

      const deleteAssetMutation = `
        mutation deleteAsset($id: ID!) {
          deleteAsset(id: $id) {
            id
          }
        }
      `;

      const response = await callGraphQLApi<AssetQueryResponse>({
        actionTypes: ['', '', ''],
        query: assetQuery,
        variables: { id: tdo?.id },
        dispatch,
        getState,
      });
      if (!response?.temporalDataObject) {
        throw new Error('Failed to fetch assets');
      }

      const {
        temporalDataObject: {
          assets: { records },
        },
      } = response;

      const deletePromises = records
        .filter(({ jsondata, isUserEdited }) => {
          const userEditedCheck = onlyUserEdited ? isUserEdited === true : true;
          return (
            detectionEngineIds.includes(jsondata.sourceEngineId) &&
            userEditedCheck
          );
        })
        .map(({ id }) =>
          callGraphQLApi({
            actionTypes: ['', '', ''],
            query: deleteAssetMutation,
            variables: { id },
            dispatch,
            getState,
          })
        );

      await Promise.all(deletePromises);

      dispatch({ type: DELETE_OBJECT_DETECTION_RESULTS_SUCCESS });
    } catch (e) {
      dispatch({ type: DELETE_OBJECT_DETECTION_RESULTS_FAILURE, payload: e });
    }
  };

/**
 * Page size and ceiling for the vtn-standard asset scan in
 * deleteTranscriptionResults. The ceiling guards against an unbounded loop, not
 * a budget: hitting it throws, because a delete that silently covers only part
 * of what it was asked to delete is the bug being fixed.
 */
const ASSET_PAGE_SIZE = 100;
const MAX_ASSET_PAGES = 100;

export const deleteTranscriptionResults =
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    try {
      const {
        transcriptionEngines,
        transcriptionEngineId,
        transcriptionCategoryId,
      } = selectConfigEngines(getState());

      const tdo = selectTdo(getState());

      /**
       * Which engines count as "the transcript" on THIS tdo.
       *
       * The CONFIGURED ids — the one the app runs today plus every id in the
       * language map — are only half of it. The configured engine moves:
       * repointing the default to Speechmatics v14 left every transcript
       * produced by the engine before it unmatched by this sweep, so Delete
       * Transcription would clear the screen, dispatch SUCCESS, and hand the
       * whole transcript back on reload. The same happens any time an org
       * changes its `transcriptionEngineId`.
       *
       * So take the tdo's OWN tasks in the transcription category as well.
       * That is the same category the loader reads results back by
       * (`saga.ts`), which makes it the same claim: whatever produced a
       * transcript here, regardless of which engine was configured when it
       * ran. No list of retired ids to keep current.
       */
      const transcribedOnThisTdo = (tdo?.tasks?.records || [])
        .filter(
          ({ engine }) =>
            !!transcriptionCategoryId &&
            engine?.categoryId === transcriptionCategoryId
        )
        .map(({ engineId }) => engineId);

      const transcriptionEngineIds = [
        ...new Set(
          [
            transcriptionEngineId,
            ...Object.values(transcriptionEngines || {}),
            ...transcribedOnThisTdo,
          ].filter(Boolean)
        ),
      ] as string[];

      // Speaker separation is derived from the transcript, so it goes with it:
      // left behind it outlives what it describes, and where the engine emits
      // words of its own the merge treats that orphan as the transcript, so the
      // text the user just deleted comes back on reload.
      //
      // By asset id rather than by adding the engine to the sweep below. The
      // sweep matches one configured engine id, so separation produced by the
      // other engine survived it (B1) — and the ids are already on the loaded
      // transcript, so there is nothing to look up.
      const speakerSeparationAssetIds =
        selectSpeakerSeparationAssetIds(getState());

      // Separation is on screen but we have no ids to delete it by — the same
      // state `deleteSpeakerSeparationResults` refuses to proceed from, and
      // for a sharper reason here. The engine sweep below matches TRANSCRIPTION
      // engines only, so these ids are the single mechanism that removes
      // separation assets. Where the engine emitted
      // words of its own, that asset IS the transcript the loader serves, so a
      // delete that skips it deletes nothing the user can see going — it
      // reports success, empties the screen, and hands the whole transcript
      // back on reload. In a disclosure tool that is the worst outcome
      // available, so fail where it can be retried instead.
      if (
        selectHasSpeakerSeparatedTranscript(getState()) &&
        !speakerSeparationAssetIds.length
      ) {
        throw new Error(
          'Speaker separation is present but carries no asset ids to delete'
        );
      }

      // Paged. `assets(...)` defaults to limit 30, and every engine result and
      // every user save writes a vtn-standard asset — so on a heavily edited
      // TDO the transcript was not on the only page this looked at, nothing
      // matched, and the delete reported success having deleted nothing while
      // the transcript stayed in aiWARE (review B2).
      //
      // `orderBy` so the pages are a stable sequence rather than whatever
      // order each call happens to return.
      const assetQuery = `
        query getAssets($id: ID!, $limit: Int, $offset: Int) {
          temporalDataObject(id: $id) {
            assets(assetType: "vtn-standard", orderBy: createdDateTime, limit: $limit, offset: $offset) {
              records {
                id
                jsondata
              }
            }
          }
        }
      `;
      interface AssetQueryResponse {
        temporalDataObject: {
          assets: {
            records: {
              id: string;
              jsondata: {
                sourceEngineId?: string;
                sourceData?: { engineId?: string };
              };
            }[];
          };
        };
      }

      const deleteAssetMutation = `
        mutation deleteAsset($id: ID!) {
          deleteAsset(id: $id) {
            id
          }
        }
      `;

      const records: AssetQueryResponse['temporalDataObject']['assets']['records'] =
        [];
      for (let page = 0; page < MAX_ASSET_PAGES; page++) {
        const response = await callGraphQLApi<AssetQueryResponse>({
          actionTypes: ['', '', ''],
          query: assetQuery,
          variables: {
            id: tdo?.id,
            limit: ASSET_PAGE_SIZE,
            offset: page * ASSET_PAGE_SIZE,
          },
          dispatch,
          getState,
        });
        if (!response?.temporalDataObject) {
          throw new Error('Failed to fetch assets');
        }

        const pageRecords = response.temporalDataObject.assets.records ?? [];
        records.push(...pageRecords);

        if (pageRecords.length < ASSET_PAGE_SIZE) {
          break;
        }

        // Out of pages rather than out of assets: throwing beats deleting what
        // we happened to see and calling it done, which is the failure this
        // pagination exists to prevent.
        if (page === MAX_ASSET_PAGES - 1) {
          throw new Error(
            `Too many assets to scan for transcription results (over ${
              MAX_ASSET_PAGES * ASSET_PAGE_SIZE
            })`
          );
        }
      }

      const deletePromises = records
        .filter(({ jsondata }) => {
          const engineId =
            jsondata?.sourceEngineId ?? jsondata?.sourceData?.engineId;
          return engineId && transcriptionEngineIds.includes(engineId);
        })
        .map(({ id }) =>
          callGraphQLApi({
            actionTypes: ['', '', ''],
            query: deleteAssetMutation,
            variables: { id },
            dispatch,
            getState,
          })
        );

      await Promise.all([
        ...deletePromises,
        ...speakerSeparationAssetIds.map((id: string) =>
          callGraphQLApi({
            actionTypes: ['', '', ''],
            query: deleteAssetMutation,
            variables: { id },
            dispatch,
            getState,
          })
        ),
      ]);

      dispatch({ type: DELETE_TRANSCRIPTION_RESULTS_SUCCESS });
    } catch (e) {
      dispatch({ type: DELETE_TRANSCRIPTION_RESULTS_FAILURE, payload: e });
    }
  };

/**
 * Delete the speaker-separation assets behind the loaded transcript, leaving
 * the transcript itself. This is the only way to run the engine again — the
 * Run option hides while a separated transcript exists — and the only way back
 * to the flat transcript for good.
 *
 * By asset id, taken from the engine results we already fetched — not by
 * sweeping the TDO's assets and matching `sourceEngineId`. That sweep was wrong
 * twice over: it matched only the configured engine, so results produced by the
 * other one were never deleted while the category-scoped loader kept finding
 * them (B1); and it read `assets(...)` with no `limit`, so on a TDO with many
 * saved assets the one it wanted was not even on the page it looked at, and the
 * delete reported success having done nothing (B2).
 *
 * Deletes every asset of the run currently shown, which is normally one. A
 * superseded run's assets are deliberately left alone — the loader already
 * ignores them, and removing data the user is not looking at is not this
 * action's job.
 */
export const deleteSpeakerSeparationResults =
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    try {
      const assetIds = selectSpeakerSeparationAssetIds(getState());

      if (!assetIds.length) {
        // Nothing to delete is not success: the results are on screen, so
        // either they predate `assetId` being requested or the field came back
        // empty. Failing here keeps the stores and the server in step rather
        // than clearing the view over data that is still there.
        throw new Error(
          'No speaker separation asset ids on the loaded transcript'
        );
      }

      const deleteAssetMutation = `
        mutation deleteAsset($id: ID!) {
          deleteAsset(id: $id) {
            id
          }
        }
      `;

      await Promise.all(
        assetIds.map((id: string) =>
          callGraphQLApi({
            actionTypes: ['', '', ''],
            query: deleteAssetMutation,
            variables: { id },
            dispatch,
            getState,
          })
        )
      );

      dispatch({ type: DELETE_SPEAKER_SEPARATION_RESULTS_SUCCESS });
    } catch (e) {
      dispatch({ type: DELETE_SPEAKER_SEPARATION_RESULTS_FAILURE, payload: e });
    }
  };

export const findTdoLock: Thunk<UpdateTDOLockRequest> =
  (newLockInfo) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const { tdoId } = newLockInfo;
    const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      query findTdoLock($schemaId: ID!, $tdoId: ID!) {
        structuredDataObjects(
          schemaId: $schemaId
          filter: { tdoId: $tdoId }
          orderBy: {
            field: modifiedDateTime
            direction: asc
          }
        ) {
          records {
            id
            data
          }
        }
      }`;

    const response = await callGraphQLApi<{
      structuredDataObjects: {
        records: [{ id: string; data: UpdateTDOLockResponse }];
      };
      errors: [Error];
    }>({
      actionTypes: ['', '', ''],
      query,
      variables: { schemaId, tdoId },
      dispatch,
      getState,
      throwOnError: true,
    });

    const error = response.errors?.[0];
    if (error) {
      dispatch(TDO_LOCK_FOUND_FAILURE({ newLockInfo, error }));
      return;
    }

    /* Find oldest, valid lock */
    const records = response?.structuredDataObjects?.records;
    const record = records?.find(({ data: { lastAccessed } }) => {
      const lockedTime = moment(lastAccessed);
      const currentTime = moment(newLockInfo.lastAccessed);
      const timeDifference = moment
        .duration(currentTime.diff(lockedTime))
        .asMilliseconds();

      const isExpired = timeDifference > LOCK_REFRESH * 5;
      return !isExpired;
    });

    if (record?.data && record?.id) {
      dispatch(
        TDO_LOCK_FOUND_SUCCESS({
          tdoLock: { ...record.data, id: record.id },
          newLockInfo,
        })
      );
    } else {
      dispatch(TDO_LOCK_FOUND_FAILURE({ newLockInfo }));
    }
  };

export interface SecureTdoLockResponse {
  createStructuredData: {
    id: string;
    data: Omit<UpdateTDOLockResponse, 'id'>;
  };
}
export const secureTdoLock: Thunk<
  Partial<UpdateTDOLockResponse> & Omit<UpdateTDOLockResponse, 'id'>
> =
  ({ id, name, tdoId, userId, lastAccessed }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      mutation updateTdoLock($schemaId: ID!, $id: ID, $name: ID!, $tdoId: ID!, $userId: ID!, $lastAccessed: String!) {
        createStructuredData(
          input: {
            id: $id
            schemaId: $schemaId
            data: {
              name: $name
              tdoId: $tdoId
              userId: $userId
              lastAccessed: $lastAccessed
            }
          }
        ) {
          id
          data
        }
      }`;

    const response = await callGraphQLApi<SecureTdoLockResponse>({
      actionTypes: ['', SECURE_TDO_LOCK_SUCCESS, SECURE_TDO_LOCK_FAILURE],
      // actionTypes: [Actions.SECURE_TDO_LOCK_START, SECURE_TDO_LOCK_SUCCESS, SECURE_TDO_LOCK_FAILURE], // TODO might add action capture schemaId
      query,
      variables: { schemaId, id, name, tdoId, userId, lastAccessed },
      dispatch,
      getState,
      throwOnError: true,
    });

    // TODO: I don't think errors is possible with `throwOnError: true` - validate
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const error = (response as any).errors?.[0];
    if (isEmpty(id) || error) {
      Actions.checkTdoLock({ name, userId, tdoId, lastAccessed });
    } else {
      dispatch(
        Actions.removeTdoLock(tdoId, userId, response?.createStructuredData?.id)
      );
    }

    return response;
  };

export const findTdoLocks: Thunk<{
  tdoId: string;
  userId: string;
}> =
  ({ tdoId, userId }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const findTdoLocksQuery = `
      query findTdoLock($schemaId: ID!, $tdoId: ID!, $userId: ID!) {
        structuredDataObjects(
          schemaId: $schemaId
          filter: { tdoId: $tdoId, userId: $userId }
          orderBy: {
            field: modifiedDateTime
            direction: asc
          }
        ) {
          records { id }
        }
      }`;

    const data = await callGraphQLApi<{
      structuredDataObjects: {
        records: [{ id: string }];
      };
      searchMedia: { jsondata: { results: [UpdateTDOLockResponse] } };
    }>({
      actionTypes: ['', '', ''],
      query: findTdoLocksQuery,
      variables: { schemaId, tdoId, userId },
      dispatch,
      getState,
    });

    dispatch(
      Actions.UPDATE_TDO_LOCKS({
        tdoLocks: data?.structuredDataObjects?.records || [],
      })
    );
  };

/** this is the new removeTdoLock function we needed to remove tdo locks faster when closing the tab by using the lock ids store in the state instead */

export const removeStoredTdoLocks: Thunk<{ dontDeleteId?: string }> =
  ({ dontDeleteId }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    // TODO this should possibly be using something faster liek a selector instead
    const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }

    const tdoLocks = selectTdoLocks(getState());

    const deleteSdoMutation = `
      mutation deleteTdoLock($schemaId: ID!, $id: ID!) {
        deleteStructuredData(input: {
          id: $id
          schemaId: $schemaId
        }) {
          id
        }
      }`;

    tdoLocks
      ?.filter(({ id }) => id !== dontDeleteId)
      .map(({ id }) => {
        void callGraphQLApi({
          actionTypes: ['', '', ''],
          query: deleteSdoMutation,
          variables: { schemaId, id },
          dispatch,
          getState,
          keepAlive: true,
        });
      });

    dispatch(
      Actions.UPDATE_TDO_LOCKS({
        tdoLocks: [],
      })
    );
  };

/** this is the original removeTdoLock function that finds and remove locks which is still uses for majority of locking functionality - TODO see if can replace with the newer functions above that split apart find and remove */
export const removeTdoLock: Thunk<{
  tdoId: string;
  userId: string;
  dontDeleteId?: string;
}> =
  ({ tdoId, userId, dontDeleteId }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const findTdoLocksQuery = `
      query findTdoLock($schemaId: ID!, $tdoId: ID!, $userId: ID!) {
        structuredDataObjects(
          schemaId: $schemaId
          filter: { tdoId: $tdoId, userId: $userId }
          orderBy: {
            field: modifiedDateTime
            direction: asc
          }
        ) {
          records { id }
        }
      }`;

    const deleteSdoMutation = `
      mutation deleteTdoLock($schemaId: ID!, $id: ID!) {
        deleteStructuredData(input: {
          id: $id
          schemaId: $schemaId
        }) {
          id
        }
      }`;

    const data = await callGraphQLApi<{
      structuredDataObjects: {
        records: [{ id: string }];
      };
      searchMedia: { jsondata: { results: [UpdateTDOLockResponse] } };
    }>({
      actionTypes: ['', '', ''],
      query: findTdoLocksQuery,
      variables: { schemaId, tdoId, userId },
      dispatch,
      getState,
    });

    const tdoLocks = data?.structuredDataObjects?.records;
    tdoLocks
      ?.filter(({ id }) => id !== dontDeleteId)
      .map(({ id }) => {
        void callGraphQLApi({
          actionTypes: ['', '', ''],
          query: deleteSdoMutation,
          variables: { schemaId, id },
          dispatch,
          getState,
        });
      });
  };

export const updateTDOStatus =
  (status: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const state = getState() as Parameters<typeof local>[0];
    const tdo = local(state).tdo;
    const tdoId = tdo?.id;
    if (!tdoId) {
      return;
    }

    const query = `
      mutation updateTDO($tdoId: ID!, $status: String) {
        updateTDO(input: {
          id: $tdoId
          status: $status
        })
        {
          id
          status
          modifiedDateTime
        }
      }
    `;

    return await callGraphQLApi<{
      updateTDO: {
        id: string;
        status: string;
      };
    }>({
      actionTypes: ['', '', ''],
      query,
      variables: {
        tdoId: tdoId,
        status,
      },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export const rotateVideo =
  (
    tdoId: string,
    rotationDegrees: number,
    detectObjects: {
      headObjects: boolean;
      personObjects: boolean;
    }
  ) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    // TODO: Type result of getState properly
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();

    const {
      glcIngestionEngineId,
      defaultClusterId,
      detectionEngineId,
      outputWriterEngineId,
    } = selectConfigEngines(state);

    const runDetection =
      detectObjects.headObjects || detectObjects.personObjects;

    const {
      faceDetectionThreshold,
      videoType,
      detectionRate,
      legacyClustering,
      clusterSimThreshold,
    } = selectGlobalSettings(state);

    const featureFlags = selectFeatureFlags(state);

    const [tasks, routes] = generateTasksAndRoutes({
      glcIngestionEngineId,
      outputWriterEngineId,
      ingestionPayload: {
        rotate: rotationDegrees,
      },
      runDetection,
      ...(runDetection && {
        detectionOptions: {
          engineId: detectionEngineId,
          confidenceThreshold: (faceDetectionThreshold || 60) / 100,
          videoType,
          detectionRate,
          detectHead: detectObjects.headObjects,
          detectNotepad: featureFlags.detectNotepads,
          detectCard: featureFlags.detectCards,
          detectPerson: detectObjects.personObjects,
          legacyClustering:
            featureFlags.devLegacyCluster && legacyClustering
              ? legacyClustering
              : undefined,
          clusterSimThreshold:
            (!featureFlags.devLegacyCluster || !legacyClustering) &&
            clusterSimThreshold
              ? clusterSimThreshold
              : undefined,
        },
      }),
    });

    const input = {
      targetId: tdoId,
      clusterId: defaultClusterId,
      tasks,
      routes,
    };

    const query = `mutation($input: CreateJob!) {
      createJob(input: $input) {
        id
        targetId
        status
      }
    }`;

    return await callGraphQLApi({
      actionTypes: [
        '',
        CREATE_ROTATE_VIDEO_JOB_SUCCESS,
        CREATE_ROTATE_VIDEO_JOB_FAILURE,
      ],
      query,
      variables: {
        input,
      },
      dispatch,
      getState,
      bailout: undefined,
    });
  };

export const checkTdoPermissions =
  ({ tdoId }: { tdoId: string }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const currentTdo = selectTdo(getState());
    if (currentTdo?.id === tdoId && isTdoInTrash(currentTdo)) {
      // The file still exists but lives in Trash — send the user to a dedicated
      // "moved to Trash" screen (with a link to the Trash page) rather than the
      // generic not-found page.
      (dispatch as (action: unknown) => void)(
        redirect({ type: ROUTE_FILE_IN_TRASH.type, payload: { tdoId } })
      );
      return;
    }

    const query = `
      query checkTdoPermissions(
        $resourceType: AuthResourceType!,
        $ids: [ID!]!,
        $permissions: [AuthPermissionType!]!
      ) {
        hasPermissions(
          resourceType: $resourceType
          ids: $ids
          permissions: $permissions
        ) {
          hasPermission
          id
        }
      }
     `;

    try {
      const tdoPermissions = await callGraphQLApi<{
        hasPermissions: { hasPermission: boolean; id: string }[];
      }>({
        actionTypes: ['', '', ''],
        query,
        variables: {
          resourceType: 'TDO',
          ids: [tdoId],
          permissions: ['AIWARE_TDO_UPDATE'],
        },
        dispatch,
        getState,
        bailout: undefined,
      });

      if (tdoPermissions?.hasPermissions) {
        dispatch(
          UPDATE_TDO_PERMISSIONS(
            tdoPermissions.hasPermissions?.[0]?.hasPermission ?? false
          )
        );
      }
    } catch (e) {
      dispatch(UPDATE_TDO_PERMISSIONS(true));
      console.debug('Error checking TDO permissions', e);
    }
  };
